// backendClient — talk to the EIX-COPILOT agent backend (server-side agent).
// ---------------------------------------------------------------------------
// When VITE_AGENT_URL is set, the browser POSTs the visible chat history to
// {VITE_AGENT_URL}/agent/chat with the provider's JWT; the backend runs the
// read-only tool loop + LLM (Groq / Bedrock / vLLM) and returns a grounded
// { answer, cards, cta, steps }. The browser holds no LLM key and no PHI logic.
//
// Blank VITE_AGENT_URL → this stays dormant and the widget uses its offline
// (sample) engine, so the demo keeps working unchanged.

const AGENT_URL = (import.meta.env.VITE_AGENT_URL || '').replace(/\/$/, '');

export const agentUrl = AGENT_URL;
export const isBackendEnabled = () => Boolean(AGENT_URL);

// Provider JWT is stored under 'sendero_token' by AuthContext.
const providerToken = () => localStorage.getItem('sendero_token') || '';

/**
 * Ask the agent backend. Maps to the same shape the widget renders offline.
 * Throws on transport / HTTP error so the caller can fall back to offline.
 * @returns {Promise<{ text, cards: Array<{type,record}>, cta?, steps: Array }>}
 */
export async function chatViaBackend({ messages, signal }) {
  const res = await fetch(`${AGENT_URL}/agent/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(providerToken() ? { Authorization: `Bearer ${providerToken()}` } : {}),
    },
    body: JSON.stringify({ messages }),
    signal,
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.detail || ''; } catch { /* ignore */ }
    throw new Error(`${res.status} ${res.statusText}${detail ? ` — ${detail}` : ''}`);
  }
  const json = await res.json();
  return {
    text: json.answer || '',
    cards: Array.isArray(json.cards) ? json.cards : [], // [{ type, record }]
    cta: json.cta || undefined,
    steps: Array.isArray(json.steps) ? json.steps : [],
  };
}

/** Best-effort health probe → { ok, model } or null if unreachable. */
export async function backendHealth(signal) {
  if (!AGENT_URL) return null;
  try {
    const res = await fetch(`${AGENT_URL}/health`, { signal });
    return res.ok ? await res.json() : null;
  } catch { return null; }
}
